// Réservation VTC multi-véhicules (une réservation → N véhicules).
// Plan : docs/to_implement/ride-multi-vehicle-booking-plan.md.
//
// SOURCE UNIQUE des constantes de plafond et de l'appariement « legs d'un même véhicule »,
// partagée par le backend (devis, checkout, emails, retrait d'un véhicule) et les deux fronts
// (agenda pro, tunnel client, page de gestion). Module « feuille » : aucune dépendance.

/**
 * Plafond DUR de véhicules par réservation, identique pour tous les pros (décision produit du
 * 22/09/2026 : pas de plafond réglable par pro). Il borne la recherche de composition, le nombre
 * de chauffeurs à verrouiller et la taille de la transaction de checkout.
 */
export const RIDE_MAX_VEHICLES_PER_BOOKING = 10;

/** Réglage VTC du pro portant l'activation du multi-véhicules (`pro_ride_config`). */
export interface RideMultiVehicleSettingLike {
  multi_vehicle_enabled?: boolean | null;
}

/**
 * Le pro accepte-t-il les réservations multi-véhicules ? Réglage `pro_ride_config.multi_vehicle_enabled`
 * (défaut true). Absent (config non chargée, ancien client) → true, comme le défaut de la colonne.
 */
export function isRideMultiVehicleEnabled(config: RideMultiVehicleSettingLike | null | undefined): boolean {
  return config?.multi_vehicle_enabled !== false;
}

/**
 * Nombre de véhicules effectivement autorisé pour ce pro : le plafond plateforme quand le réglage
 * est actif, 1 sinon (une voiture par réservation, comportement historique).
 */
export function resolveRideMaxVehicles(config: RideMultiVehicleSettingLike | null | undefined): number {
  return isRideMultiVehicleEnabled(config) ? RIDE_MAX_VEHICLES_PER_BOOKING : 1;
}

/** Position d'un véhicule : `vehicle_index` absent = course mono-véhicule (historique ou agenda). */
export const RIDE_DEFAULT_VEHICLE_INDEX = 1;

export function resolveRideVehicleIndex(vehicleIndex: number | null | undefined): number {
  return typeof vehicleIndex === 'number' && Number.isInteger(vehicleIndex) && vehicleIndex > 0
    ? vehicleIndex
    : RIDE_DEFAULT_VEHICLE_INDEX;
}

/** Trajet (leg) d'une course, tel qu'exposé par l'API (`AppointmentRideDetailsResponseSchema`). */
export interface RideLegLike {
  trip_leg?: string;
  vehicle_index?: number | null;
}

export interface RideVehicleGroup<TLeg extends RideLegLike> {
  /** Position du véhicule dans la réservation (1..N). */
  vehicle_index: number;
  /** Legs de CE véhicule, dans l'ordre de la source (aller puis retour). */
  legs: TLeg[];
}

/**
 * Regroupe les legs d'une réservation PAR VÉHICULE — seule source de vérité de l'appariement
 * aller/retour. Sans elle, « l'autre RDV de la réservation » est pris pour le retour, ce qui
 * inverse les adresses de deux véhicules faisant un aller simple (cf. EditRideAppointmentForm).
 *
 * Un aller-retour SANS heure de retour porte ses deux legs sur le même RDV : ils partagent alors
 * le même `vehicle_index` et se retrouvent dans le même groupe, comme attendu.
 *
 * Groupes triés par position croissante ; l'ordre des legs d'un groupe suit celui de l'entrée.
 */
export function groupRideLegsByVehicle<TLeg extends RideLegLike>(legs: readonly TLeg[]): Array<RideVehicleGroup<TLeg>> {
  const byIndex = new Map<number, TLeg[]>();
  for (const leg of legs) {
    const index = resolveRideVehicleIndex(leg.vehicle_index);
    const group = byIndex.get(index);
    if (group) group.push(leg);
    else byIndex.set(index, [leg]);
  }
  return [...byIndex.entries()]
    .sort(([left], [right]): number => left - right)
    .map(([vehicle_index, groupLegs]): RideVehicleGroup<TLeg> => ({ vehicle_index, legs: groupLegs }));
}

/**
 * Nombre de véhicules d'une réservation (1 pour une course mono ou historique).
 */
export function countRideVehicles(legs: readonly RideLegLike[]): number {
  return legs.length === 0 ? 0 : new Set(legs.map((leg): number => resolveRideVehicleIndex(leg.vehicle_index))).size;
}

/* ──────────────────────────────────────────────────────────────────────────────────────────
 *  Faisabilité d'une composition — MÊME règle que le planificateur backend (§2.3 du plan,
 *  `ride-vehicle-plan.ts`). Partagée pour que le tunnel client refuse (et n'accepte) EXACTEMENT
 *  ce que le backend refusera : sans source unique, le front et le planificateur dérivent et le
 *  client découvre `RIDE_GROUP_TOO_LARGE` seulement au devis.
 * ────────────────────────────────────────────────────────────────────────────────────────── */

/** Bornes d'une classe de véhicule, telles qu'exposées par la config publique du tunnel. */
export interface RideCompositionBoundsLike {
  pax_min?: number | null;
  pax_max?: number | null;
  small_luggage_min?: number | null;
  small_luggage_max?: number | null;
  large_luggage_min?: number | null;
  large_luggage_max?: number | null;
}

export interface RideLuggageCountsLike {
  luggage_count?: number | null;
  small_luggage_count?: number | null;
  large_luggage_count?: number | null;
}

/** Legacy courses retain their unclassified total; split requests derive their total from both sizes. */
export function resolveRideLuggageCounts(group: RideLuggageCountsLike): {
  luggage_count: number | null;
  small_luggage_count: number | null;
  large_luggage_count: number | null;
} {
  if (group.small_luggage_count != null || group.large_luggage_count != null) {
    const small = Math.max(0, toCompositionInt(group.small_luggage_count, 0));
    const large = Math.max(0, toCompositionInt(group.large_luggage_count, 0));
    return { luggage_count: small + large, small_luggage_count: small, large_luggage_count: large };
  }
  return {
    luggage_count: group.luggage_count == null ? null : Math.max(0, toCompositionInt(group.luggage_count, 0)),
    small_luggage_count: null,
    large_luggage_count: null,
  };
}

/** Une entrée de l'affichage des bagages d'une course (agenda pro, page client, emails, tunnel). */
export interface RideLuggageDisplayPart {
  size: 'small' | 'large' | 'total';
  count: number;
}

/**
 * SOURCE UNIQUE de l'affichage des bagages d'une course. Une course historique (total non classé)
 * affiche son total ; une course aux tailles saisies n'affiche que les tailles non nulles et retombe
 * sur un total à 0 (« Aucun bagage ») quand les deux sont à zéro — jamais deux lignes à 0.
 */
export function rideLuggageDisplayParts(group: RideLuggageCountsLike): RideLuggageDisplayPart[] {
  const counts = resolveRideLuggageCounts(group);
  if (counts.small_luggage_count === null || counts.large_luggage_count === null) {
    return counts.luggage_count === null ? [] : [{ size: 'total', count: counts.luggage_count }];
  }
  const parts: RideLuggageDisplayPart[] = [];
  if (counts.small_luggage_count > 0) parts.push({ size: 'small', count: counts.small_luggage_count });
  if (counts.large_luggage_count > 0) parts.push({ size: 'large', count: counts.large_luggage_count });
  return parts.length > 0 ? parts : [{ size: 'total', count: 0 }];
}

/** Une classe de véhicule et le nombre d'unités demandées (`vehicles: [{ vehicle_id, quantity }]`). */
export interface RideCompositionItem<TVehicle extends RideCompositionBoundsLike = RideCompositionBoundsLike> {
  vehicle: TVehicle;
  quantity: number;
}

/** Somme des bornes d'une composition. `Infinity` = axe NON contraint (borne absente en base). */
export interface RideCompositionBounds {
  passenger_min: number;
  passenger_max: number;
  small_luggage_min: number;
  small_luggage_max: number;
  large_luggage_min: number;
  large_luggage_max: number;
  /** Unités retenues au total (les quantités ≤ 0 sont ignorées, comme côté planificateur). */
  vehicle_count: number;
  /**
   * Une classe demandée a des bornes incohérentes (`pax_min > pax_max`) : le planificateur
   * l'écarte de ses candidats, donc une composition qui la contient n'est jamais réalisable.
   */
  has_unusable_vehicle: boolean;
}

/** Plancher passagers d'un véhicule : pas de véhicule « bagages seuls » (décision du 22/09/2026). */
const RIDE_VEHICLE_MIN_PASSENGERS = 1;

function toCompositionInt(value: number | null | undefined, fallback: number): number {
  if (value === null || value === undefined) return fallback;
  const truncated = Math.trunc(value);
  return Number.isFinite(truncated) ? truncated : fallback;
}

/** Borne haute : absente (ou non finie) = pas de contrainte sur cet axe. */
function toCompositionUpperBound(value: number | null | undefined): number {
  if (value === null || value === undefined) return Number.POSITIVE_INFINITY;
  const truncated = Math.trunc(value);
  return Number.isFinite(truncated) ? Math.max(0, truncated) : Number.POSITIVE_INFINITY;
}

/**
 * Bornes cumulées d'une composition — base du test de faisabilité ET du plafond des compteurs
 * du tunnel en mode composition explicite (Σ `pax_max` × quantité).
 */
export function sumRideCompositionBounds(items: readonly RideCompositionItem[]): RideCompositionBounds {
  const bounds: RideCompositionBounds = {
    passenger_min: 0,
    passenger_max: 0,
    small_luggage_min: 0,
    small_luggage_max: 0,
    large_luggage_min: 0,
    large_luggage_max: 0,
    vehicle_count: 0,
    has_unusable_vehicle: false,
  };
  for (const item of items) {
    const quantity = toCompositionInt(item.quantity, 0);
    if (quantity <= 0) continue;
    const paxMin = Math.max(RIDE_VEHICLE_MIN_PASSENGERS, toCompositionInt(item.vehicle.pax_min, 0));
    const paxMax = toCompositionUpperBound(item.vehicle.pax_max);
    const smallMin = Math.max(0, toCompositionInt(item.vehicle.small_luggage_min, 0));
    const smallMax = toCompositionUpperBound(item.vehicle.small_luggage_max);
    const largeMin = Math.max(0, toCompositionInt(item.vehicle.large_luggage_min, 0));
    const largeMax = toCompositionUpperBound(item.vehicle.large_luggage_max);
    if (paxMin > paxMax || smallMin > smallMax || largeMin > largeMax) {
      bounds.has_unusable_vehicle = true;
      continue;
    }
    bounds.vehicle_count += quantity;
    bounds.passenger_min += paxMin * quantity;
    bounds.passenger_max += paxMax * quantity;
    bounds.small_luggage_min += smallMin * quantity;
    bounds.small_luggage_max += smallMax * quantity;
    bounds.large_luggage_min += largeMin * quantity;
    bounds.large_luggage_max += largeMax * quantity;
  }
  return bounds;
}

/**
 * Une composition peut-elle transporter ce groupe ?
 *
 * Chaque véhicule accepte un intervalle CONTINU de passagers et de chaque taille de bagages, indépendamment :
 * les totaux atteignables forment donc exactement [Σ minimums, Σ maximums] — inutile d'énumérer
 * les répartitions, le test est linéaire. C'est mot pour mot la règle du planificateur backend.
 *
 * Sans tailles déclarées, `luggage_count` est comparé aux bornes des grands bagages pour les anciens clients.
 * Aucune taille n’est déduite pour les historiques. Absent/null = axe non contraint.
 */
export function isRideCompositionFeasible(
  items: readonly RideCompositionItem[],
  group: { passenger_count: number } & RideLuggageCountsLike,
): boolean {
  const bounds = sumRideCompositionBounds(items);
  if (bounds.has_unusable_vehicle || bounds.vehicle_count < 1) return false;
  const passengers = toCompositionInt(group.passenger_count, 0);
  if (bounds.passenger_min > passengers || bounds.passenger_max < passengers) return false;
  const counts = resolveRideLuggageCounts(group);
  if (counts.small_luggage_count !== null && counts.large_luggage_count !== null) {
    return (
      bounds.small_luggage_min <= counts.small_luggage_count &&
      bounds.small_luggage_max >= counts.small_luggage_count &&
      bounds.large_luggage_min <= counts.large_luggage_count &&
      bounds.large_luggage_max >= counts.large_luggage_count
    );
  }
  if (counts.luggage_count === null) return true;
  const luggage = counts.luggage_count;
  return bounds.large_luggage_min <= luggage && bounds.large_luggage_max >= luggage;
}

/**
 * Le groupe tient-il dans UN SEUL de ces véhicules ? Pour un groupe d'au moins un passager, c'est
 * exactement la règle de la sélection MONO du backend (`vehicleMatchesRideRequest`) : passagers
 * dans [pax_min, pax_max], chaque taille déclarée dans ses bornes (ou le total historique
 * dans les bornes des grands bagages), borne absente = axe non contraint.
 *
 * Aiguillage du tunnel (garde-fou du 23/09/2026) : un groupe qui tient dans un véhicule reste sur
 * le chemin mono, déjà en production ; seul un groupe que le mono refuse (`RIDE_VEHICLE_NOT_FOUND`)
 * demande la répartition. Des bornes AGRÉGÉES (celles d'une formule : min des mins, max des maxes)
 * ne font qu'élargir la réponse « tient », donc renvoient vers le mono, jamais l'inverse.
 * Aucune borne connue (liste vide) → true : on reste sur le comportement historique.
 */
export function rideGroupFitsSingleVehicle(
  vehicles: readonly RideCompositionBoundsLike[],
  group: { passenger_count: number } & RideLuggageCountsLike,
): boolean {
  if (vehicles.length === 0) return true;
  return vehicles.some((vehicle): boolean => isRideCompositionFeasible([{ vehicle, quantity: 1 }], group));
}
